/**
 * Size tokens for the `Avatar` component, covering both standard avatar sizes
 * and a set of favicon-style sizes (`FAV_ICON_*`) used for compact brand logos.
 *
 * Pass any value from this object as the `size` prop of `Avatar`, `AvatarGroup`,
 * or `AvatarWrapped`. Each token maps to a CSS class defined in the stylesheet.
 * Defaults to `Size.NORMAL` when the `size` prop is omitted.
 *
 * Standard sizes (smallest → largest): `X_SMALL` → `SMALL` → `MEDIUM` → `NORMAL` → `LARGE` → `X_LARGE` → `XX_LARGE`.
 * Favicon sizes (smallest → largest): `FAV_ICON_XXS` → `FAV_ICON_XS` → `FAV_ICON_S` → `FAV_ICON_SM` → `FAV_ICON_M` → `FAV_ICON_L` → `FAV_ICON_XL` → `FAV_ICON_XLS` → `FAV_ICON_XXL` → `FAV_ICON_XXXL`.
 */
// Add new names in avatar.module.styl with the same name from Size object, to avoid creating extra variables.
export const Size = {
    X_SMALL: 'X_SMALL',
    SMALL: 'SMALL',
    MEDIUM: 'MEDIUM',
    NORMAL: 'NORMAL',
    LARGE: 'LARGE',
    X_LARGE: 'X_LARGE',
    XX_LARGE: 'XX_LARGE',
    FAV_ICON_XXS: 'FAV_ICON_XXS',
    FAV_ICON_XS: 'FAV_ICON_XS',
    FAV_ICON_S: 'FAV_ICON_S',
    FAV_ICON_SM: 'FAV_ICON_SM',
    FAV_ICON_M: 'FAV_ICON_M',
    FAV_ICON_L: 'FAV_ICON_L',
    FAV_ICON_XL: 'FAV_ICON_XL',
    FAV_ICON_XLS: 'FAV_ICON_XLS',
    FAV_ICON_XXL: 'FAV_ICON_XXL',
    FAV_ICON_XXXL: 'FAV_ICON_XXXL',
} as const;

export const SizesVariants = [...Object.values(Size)] as const;

/**
 * Union of all `Size` object values — every valid token that can be passed as
 * the `size` prop to `Avatar`, `AvatarGroup`, or `AvatarWrapped`.
 */
export type SizesVariantsTypes = typeof SizesVariants[number];

export const SIZE_CLASSES: Record<SizesVariantsTypes, string> = Object.fromEntries(
    Object.keys(Size).map(key => [key, `avatar_${key.toLowerCase().replace(/_/g, '-')}`]),
) as Record<SizesVariantsTypes, string>;

/**
 * Shape variants for the avatar border-radius:
 * - `"rounded"` (default) — fully rounded (pill/circle) corners.
 * - `"square"` — sharp square corners.
 */
export type Shape = 'rounded' | 'square';
export const SHAPE_CLASSES = {
    rounded: 'avatar_rounded',
    square: 'avatar_square',
} as const;

export const CURRENT_USER_COLOR = 'blue';
export const INITIAL_COLORS: Array<string> = [
    'green',
    'skyBlue',
    'lightBlue',
    'yellow',
    'aqua',
    'tropicalForest',
    'plum',
];
